import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ClickAwayListener, FormHelperText, TextField } from '@material-ui/core';
import { searchAutoCompleteApi } from 'api/search';
import SkeletonLoader from 'components/SkeletonLoader';
import HashTag from 'models/hashtag';
import { useInView } from 'react-intersection-observer';
import { useDispatch } from 'react-redux';
import AsyncCreatableSelect from 'react-select/async-creatable';
import { StyledDimensionRow } from 'screens/admin/Growth/GrowthTable/GrowthTableHelperComps';
import styled, { css } from 'styled-components';
import { StyledBorderedPaper, StyledBox } from 'styles/common';

import { MESH_TAG_TYPES } from 'constants/ProjectConstants';
import { haveSamePropValues } from 'constants/helper';
import theme from 'constants/theme';

import { updateCompetency } from 'services/admin';
import {
  disableCompetencyRow,
  getCompetencyDescription,
  saveCompetency,
  enableCompetencyRow,
} from 'services/competencies';
import { showErrorSnackbar } from 'services/snackbar';

import { Typography13 } from 'uiToolkit/Typography';

import DescriptionCells from '../Description/DescriptionCells';

const CORE_COMPETENCIES_TAG = 'CORE_COMPETENCIES_TAG';
const CORE_COMPETENCIES_NAME = 'CORE_COMPETENCIES_NAME';

const DISPLAY_NAME_ERROR = 'DISPLAY_NAME_ERROR';
const NAME_ERROR = 'NAME_ERROR';

const StyledColumnCell = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  justify-content: stretch;

  width: 280px;
  min-height: 10rem;

  padding: 1.5rem;
  border: ${(props) => `1px solid ${props.theme.palette.borders.lighterGrey}`};
  border-left-width: 0px;

  background-color: white;

  ${(props) =>
    props.fixed &&
    css`
      width: 255px;
      position: sticky;
      left: 0px;
      z-index: 1;
      border-left-width: 1px;
    `};
`;

const StyledTextField = styled(TextField)`
  input {
    color: ${(props) => (props.isTextNewDimension ? props.theme.palette.grey.main : 'inherit')};
    font-size: 16px;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
    padding: 0 !important;
  }
  .MuiOutlinedInput-notchedOutline {
    border: none;
    outline: none;
  }
  .MuiOutlinedInput-root {
    padding: 0 !important;
  }
  .MuiAutocomplete-inputRoot {
    padding: 0 !important;
  }
  ${(props) =>
    props.$disabledBlackColor &&
    css`
      input {
        color: ${theme.palette.text.blackv1};
      }
    `}
`;

const DisableCompetencyRow = ({ functionId, competencyId, fetchCompetency, isEntireRowDisabled = false }) => {
  const onDisable = async () => {
    try {
      const res = await disableCompetencyRow({ functionId, competencyId });
      fetchCompetency();
    } catch (err) {
      console.error('Error disabling the competency ', err);
    }
  };

  const onEnable = async () => {
    try {
      const res = await enableCompetencyRow({ functionId, competencyId });
      fetchCompetency();
    } catch (err) {
      console.error('Error enabling the competency ', err);
    }
  };

  return (
    <DisableCompetencyContainer $noBorder style={{ padding: 0 }}>
      <Typography13 customColor={theme.palette.text.label}>
        Click <TextButton onClick={isEntireRowDisabled ? onEnable : onDisable}>here</TextButton> to{' '}
        {isEntireRowDisabled ? 'enable' : 'disable'} this competency
      </Typography13>
    </DisableCompetencyContainer>
  );
};

const CompetencyNameAndHashtag = ({
  tag,
  isEditing,
  setIsEditing,
  handleChange,
  updateTag,
  setCurrentTag,
  isNewComp = false,
  errorType,
  hasError,
  errorMessage,
  scrollTableHeaderIntoView = () => {},
  competencies = [],
  setCompetencyAlreadyExists = () => {},
  setCompetency = () => {},
  validationError = false,
}) => {
  // const [competencies, setCompetencies] = useState([]);

  const displayNameRef = useRef();

  useEffect(() => {
    if (isNewComp) {
      displayNameRef.current.focus();
      scrollTableHeaderIntoView();
    }
  }, []);

  const getCompetencyData =
    (tagType = CORE_COMPETENCIES_TAG) =>
    (text) =>
      searchAutoCompleteApi({
        tagType,
        text,
        limit: 5,
        widgetType: 'ORGANIZATION_SETTING',
      }).then((res) => {
        const options = res.entity?.map(({ orgTag }) => {
          const hashTag = new HashTag();
          hashTag.fromJSON(orgTag);

          const stringifiedCompetenciesArray = competencies.map((id) => String(id));

          if (tagType === CORE_COMPETENCIES_TAG) {
            return {
              label: orgTag.name,
              value: orgTag.uuid,
              isDisabled: stringifiedCompetenciesArray.includes(hashTag.id),
              hashTag,
            };
          }
          return {
            label: orgTag.displayName,
            value: orgTag.uuid,
            isDisabled: stringifiedCompetenciesArray.includes(hashTag.id),
            hashTag,
          };
        });
        return options;
      });

  const handleKeyDown = ({ keyCode, key, target }) => {
    if (keyCode === 27) {
      // this will work since everytime update is passed up to parent.
      target.blur();
    }
    if (key === 'Enter') {
      updateTag(tag);
    }
  };

  return (
    <div>
      {(isEditing || isNewComp) && (
        <MeshAsyncCreatableSelect
          classNamePrefix="select"
          // styles={customStyles}
          placeholder="Competency"
          onChange={({ value, label, __isNew__, hashTag }) => {
            if (__isNew__) {
              handleChange({ name: 'displayName', value: label });
              setCompetencyAlreadyExists(false);
            } else {
              setCurrentTag(hashTag);
              setCompetency((prev) => ({ description: {}, organizationTag: hashTag }));
              setCompetencyAlreadyExists(true);
            }
          }}
          name="displayName"
          value={tag.displayName ? { value: tag.displayName, label: tag.displayName } : null}
          loadOptions={getCompetencyData(CORE_COMPETENCIES_NAME)}
          isDisabled={!isNewComp}
          width="100%"
          focus={isNewComp}
          ref={displayNameRef}
        />
      )}
      {!(isEditing || isNewComp) && (
        <StyledTextField
          name="displayName"
          placeholder="Competency"
          fullWidth
          variant="outlined"
          value={tag.displayName}
          onKeyDown={(event) => handleKeyDown({ event })}
          onChange={(e) => handleChange({ name: 'name', value: e.target.value })}
          autoFocus
          disabled
          $disabledBlackColor
        />
      )}
      {hasError && errorType === DISPLAY_NAME_ERROR && (
        <FormHelperText style={{ color: theme.palette.text.red }}>This is a mandatory field</FormHelperText>
      )}

      <TextFieldContainer isEditing={isEditing}>
        <HashTagText>#</HashTagText>
        <StyledTextField
          name="name"
          placeholder="Hashtag"
          fullWidth
          variant="outlined"
          value={tag.name}
          onKeyDown={(event) => handleKeyDown({ event })}
          onChange={(e) => handleChange({ name: 'name', value: e.target.value })}
          autoFocus
          disabled={!isNewComp}
          $disabledBlackColor
        />
      </TextFieldContainer>
      {hasError && errorType === NAME_ERROR && (
        <FormHelperText style={{ color: theme.palette.text.red }}>This is a mandatory field</FormHelperText>
      )}
      {validationError && (
        <FormHelperText style={{ color: theme.palette.text.red }}>
          Space and special characters are not allowed
        </FormHelperText>
      )}
    </div>
  );
};

const CompetencyCell = ({
  competency,
  loading,
  updateTag,
  addNew = false,
  isNewComp,
  scrollTableHeaderIntoView,
  competencies,
  setCompetency,
  setIsNewComp,
  functionId,
  fetchCompetency,
}) => {
  const tag = competency?.organizationTag ?? new HashTag();
  const [currentTag, setCurrentTag] = useState(tag);
  const [isEditing, setIsEditing] = useState(false);

  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [errorType, setErrorType] = useState(null);

  const [validationError, setValidationError] = useState(false);

  // if user is adding already existing competency to selected function
  const [competencyAlreadyExists, setCompetencyAlreadyExists] = useState(false);

  const toggleEditing = () => {
    setIsEditing((prev) => !prev);
  };

  useEffect(() => {
    setCurrentTag(tag);
  }, [tag.active, tag.name, tag.description, tag.id, tag.displayName, isEditing]);

  const handleChange = ({ name, value }) => {
    setValidationError(false);
    if (name === 'name') {
      const regex = /^[a-zA-Z0-9_\b\*]+$/;
      setValidationError(false);

      if (value === '' || regex.test(value)) {
        setCurrentTag((prev) => ({ ...prev, [name]: value }));
      } else {
        setValidationError(true);
        setTimeout(() => setValidationError(false), 2500);
      }
    } else {
      setCurrentTag((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleClickAway = useCallback(() => {
    const propertyList = ['name', 'description', 'jobFunctionRole', 'displayName'];
    if (!competencyAlreadyExists) {
      if (haveSamePropValues(tag, currentTag, propertyList)) {
        setIsEditing(false);
        setHasError(false);
        setErrorMessage('');
      } else {
        // if (isEditing) {
        if (currentTag.displayName && currentTag.name) {
          setHasError(false);
          setErrorMessage('');
          updateTag(currentTag);
          setIsEditing(false);
        } else {
          setHasError(true);
          if (!currentTag.displayName) {
            setErrorMessage('This is a required field');
            setErrorType(DISPLAY_NAME_ERROR);
          } else if (!currentTag.name) {
            setErrorMessage('This is a required field');
            setErrorType(NAME_ERROR);
          }
        }
        // }
      }
    } else {
      setIsEditing(false);
      setIsNewComp(false);
    }
  }, [currentTag, updateTag, competencyAlreadyExists]);

  if (loading) {
    return <SkeletonLoader skeletons={['100%', '100%']} />;
  }

  return (
    <ClickAwayListener onClickAway={handleClickAway}>
      <StyledColumnCell style={{ paddingBottom: '0.75rem' }} fixed isEditing={isEditing}>
        <StyledBox bottomGutter={2}>
          <CompetencyNameAndHashtag
            isEditing={isEditing}
            setIsEditing={setIsEditing}
            tag={currentTag}
            handleChange={handleChange}
            updateTag={updateTag}
            setCurrentTag={setCurrentTag}
            isNewComp={isNewComp}
            hasError={hasError}
            errorType={errorType}
            errorMessage={errorMessage}
            scrollTableHeaderIntoView={scrollTableHeaderIntoView}
            competencies={competencies}
            setCompetencyAlreadyExists={setCompetencyAlreadyExists}
            setCompetency={setCompetency}
            validationError={validationError}
          />
        </StyledBox>
        <StyledBox flex="1" display="flex" alignItems="stretch" justifyContent="stretch" bottomGutter={1}>
          {/* <DimensionDescription description={description} onEdit={handleDescriptionEdit} /> */}
        </StyledBox>

        <StyledBox display="flex" alignItems="center" justifyContent="space-between">
          {/* <DimensionContentType contentType={contentType} onChange={handleContentTypeChange} /> */}
        </StyledBox>
        {functionId && (
          <DisableCompetencyRow
            functionId={functionId}
            competencyId={currentTag.id}
            fetchCompetency={fetchCompetency}
            isEntireRowDisabled={currentTag?.isEntireRowDisabled}
          />
        )}
      </StyledColumnCell>
    </ClickAwayListener>
  );
};

const CompetencyFunctionAndLevelRow = ({
  id,
  functionId,
  roles = [],
  fetchCompetencySummary,
  addNew,
  removeNewTag,
  setCompetencySummary,
  setAddNew,
  scrollTableHeaderIntoView,
  competencies = [],
}) => {
  const { ref, inView } = useInView({ triggerOnce: true });

  const [isNewComp, setIsNewComp] = useState(id === 'newComp');

  const dispatch = useDispatch();

  const [competency, setCompetency] = useState(new HashTag());
  const [loading, setLoading] = useState(false);

  const teamId = null;

  const tagType = MESH_TAG_TYPES[2].value;

  const fetchCompetency = () => {
    setLoading(true);
    getCompetencyDescription({
      competencyId: id === 'newComp' ? competency?.organizationTag?.id : id,
      functionId,
    })
      .then((res) => {
        setCompetency(res);
      })
      .catch((err) => console.error('Error in fetching competency : ', err))
      .finally(() => setLoading(false));
  };

  const addTag = (tag) => {
    setLoading(true);
    saveCompetency({ ...tag }, teamId, tagType)
      .then((orgTags) => {
        setCompetencySummary((prev) => {
          const { competencies } = prev;
          const updatedCompetencies = competencies?.filter((id) => id !== 'newComp');
          return { ...prev, competencies: [orgTags.id, ...updatedCompetencies] };
        });
        setAddNew(false);
        setCompetency(orgTags);
        // fetchCompetencySummary();
        // removeNewTag();
      })
      .catch((err) => {
        dispatch(showErrorSnackbar(err.message));
      })
      .finally(() => setLoading(false));
  };

  const updateTag = (tag) => {
    setLoading(true);
    updateCompetency({ ...tag }, teamId, tagType)
      .then((orgTags) => {
        fetchCompetency();
      })
      .catch((err) => {
        dispatch(showErrorSnackbar(err.message));
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (inView) {
      fetchCompetency();
    }
  }, [inView]);

  const rolesToLoopFrom = useMemo(() => (functionId ? roles : ['allFunctions']), [functionId, roles]);

  if (loading) {
    return (
      <StyledBorderedPaper style={{ width: '100%', height: '200px' }}>
        <SkeletonLoader skeletons={['100%', '100%']} />
      </StyledBorderedPaper>
    );
  }

  return (
    <StyledDimensionRow ref={ref}>
      <CompetencyCell
        loading={loading}
        competency={competency}
        updateTag={isNewComp ? addTag : updateTag}
        addNew={addNew}
        isNewComp={isNewComp}
        scrollTableHeaderIntoView={scrollTableHeaderIntoView}
        competencies={competencies}
        setCompetency={setCompetency}
        setIsNewComp={setIsNewComp}
        functionId={functionId}
        fetchCompetency={fetchCompetency}
      />

      <DescriptionCells
        roles={rolesToLoopFrom}
        fetchCompetency={fetchCompetency}
        functionId={functionId}
        description={competency?.description}
        competencyId={competency?.organizationTag?.id}
        setAddNew={setAddNew}
      />
    </StyledDimensionRow>
  );
};

const TextFieldContainer = styled.div`
  display: flex;
  align-items: center;

  flex: 1;

  margin-top: 0.5rem;
  margin-bottom: 0.5rem;

  padding-left: 0.25rem;
  min-height: 2.5rem;

  ${(props) =>
    props.isEditing &&
    css`
      background-color: ${props.theme.palette.background.paper};
      border-radius: 4px;
      border: 1px solid ${theme.palette.borders.lighterGrey};
    `};
`;

const HashTagText = styled.span`
  // padding-top: 6px;
  display: inline-block;
  padding-right: 2px;
`;

export const MeshAsyncCreatableSelect = styled(AsyncCreatableSelect)`
  margin-bottom: 0.6em;
  // z-index: 9;
  width: ${(props) => props.width ?? '72%'};
  display: inline-block;
  margin: 1% 2% 1% 0;
`;

export const TextButton = styled.span`
  text-decoration: underline;
  color: ${theme.palette.primary.main};
  cursor: pointer;
`;

export const DisableCompetencyContainer = styled.div`
  margin-top: auto;
  background: ${theme.palette.background.paper};
  padding: 0rem 1.5rem;
  ${(props) =>
    !props.$noBorder &&
    css`
      border-right: 1px solid ${theme.palette.borders.lighterGrey};
    `}
`;

export default CompetencyFunctionAndLevelRow;
